import { useState, useEffect } from "react";

import {
  Button,
  Typography,
  ArrowButton,
  ExpandablePanel,
  ExpandablePanelBody,
  ExpandablePanelHeader,
  Modal,
  Select,
  SelectEvent,
  TextField, TextArea, InfoPanel, Table, useTable, TableMetadata, DocumentList
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Upload.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { DocumentData } from "@king-ict/eupisi-ui/models/Document/DocumentData";
import { codeBookToSelectData, hrDate, nameof } from "@king-ict/eupisi-ui/utils";
import { saveAs } from "file-saver";
import type { TFunction } from "i18next";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, useLocation } from "react-router";
import * as yup from "yup";

import {
  getInstitutionRequest, getProgramRequestDetail, DocumentUploadRequest, uploadDocument, createProgramRequestCorrection,
  downloadDocument
} from "../../api/nisooApi";
import { DocumentsUploadModal } from "../../components/DocumentsUploadModal";
import OtherData from "../../components/OtherData";
import { ProgramRequestHeader } from "../../components/ProgramRequestHeader";
import StaticField from "../../components/StaticField";
import UploadButton from "../../components/UploadButton";
import { useInput } from "../../hooks";
import { DocumentType } from "../../models/Document";
import {
  Institution, InstitutionLocation,
  institutionInitial
} from "../../models/Institution";
import { ProgramType } from "../../models/Program";
import {
  ProgramRequestCorrectionValidationScheme,
  OtherEnrollmentConditionId, ProgramRequestData,
  createProgramRequestData, ProgramRequest,
  ProgramRequestDetail, createProgramRequestInitial,
  mapProgramRequestData, createProgramRequestCorrectionFromProgramRequest,
  convertFromProgramRequest,
  PreviousPage,
  Competence, OtherFormOfTeachingId, OtherKnowledgeCheckId,
  ProgramRequestType
} from "../../models/ProgramRequest";
import { AdditionalDocumentation } from "../../models/ProgramRequest/AdditionalDocumentation";
import { AdditionalDocumentationType } from "../../models/ProgramRequest/AdditionalDocumentationType";
import { createProgramRequestDetailInitial } from "../../models/ProgramRequest/ProgramRequestDetail";
import { ProgramRequestStatus } from "../../models/ProgramRequest/ProgramRequestStatus";
import { RoleEnum } from "../../models/User/RoleEnum";
import {
  useGetProgramsByTypeAndDateQuery,
  useGetFormsOfTeachingQuery, useGetKnowledgeChecksQuery,
  useGetEnrollmentConditionsQuery, useGetLanguageLevelsQuery
}
  from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { dashboardRoute, programRequestListRoute, programRequestDetailsUrl, newProgramRequestListRoute } from "../../routes/routes";
import InstitutionPanel from "../Institution/InstitutionPanel";
import { OtherLegalEntityPanel } from "../ProgramRequest/OtherLegalEntityPanel";
import { ProgramRequestHistoryPanel } from "../ProgramRequest/ProgramRequestHistoryPanel";
import { ProgramRequestTeacherPanel } from "../ProgramRequest/ProgramRequestTeacherPanel";
import { CompetenceInfoMzo } from "./CompetenceInfoMzo";
import { ExistingProgramInfo } from "./ExistingProgramInfo";
import { ExpertiseEditProgramInfo } from "./ExpertiseEditProgramInfo";
import { OtherEnrollmentConditions } from "./OtherEnrollmentConditionsEditor";
import ProgramRequestModuleEditor from "./ProgramRequestModuleEditor";
import styles from "./ProgramRequestMzo.module.scss";
import ProgramRequestTeacherMzo from "./ProgramRequestTeacherMzo";

const ResolverTeacherValidationScheme = ({
  t,
}: {
  t: TFunction;
}) => {
  const requiredMsg = t("validations.required");
  const ensambleTooLongMsg = t("validations.maximalStringLength", { maxLength: 500 });

  return yup.object().shape({
    oib: yup.string().required(requiredMsg),
    firstName: yup.string().required(requiredMsg),
    lastName: yup.string().required(requiredMsg),
    programRequestClassEnsambles: yup
      .array()
      .of(
        yup.object().shape({
          ensamble: yup.string().required(requiredMsg).max(500, ensambleTooLongMsg),
        }),
      )
      .min(1, requiredMsg),
  });
};

const ResolverProgramRequestCorrectionValidationScheme = ({
  t,
}: {
  t: TFunction;
}) =>
  yup.object().shape({
    teachers: yup.array().of(ResolverTeacherValidationScheme({ t })),
  });


export default function ProgramRequestMzo() {
  const { t } = useTranslation();
  const history = useHistory();

  const params = useParams<{ programRequestId: string }>();
  const location = useLocation<{ previousPage: PreviousPage }>();
  const { data: userSession } = useGetUserSessionQuery();
  const [institution, setInstitution] = useState<Institution>(institutionInitial);
  const [programRequest, setProgramRequest] = useState<ProgramRequest>(createProgramRequestInitial);
  const [programRequestDetail, setProgramRequestDetail] = useState<ProgramRequestDetail>(createProgramRequestDetailInitial);
  const [programRequestCorrection, setProgramRequestCorrection] = useState<ProgramRequestData>(createProgramRequestData);

  const formatedProgramRequestDate = (): string => {
    if (programRequest.requestDate) {
      var requestDate = new Date(programRequest.requestDate);
      return requestDate.toISOString().split('T')[0];
    }
    return "";
    
  };

  let formatedReqestDate = formatedProgramRequestDate();
  const isSubmittedMzoStatus = programRequest.requestStatusId === ProgramRequestStatus.SubmittedMZO;
  // Teacher data is not editable during ASOO harmonization
  const isHarmonizationAsooEdit = programRequest.requestStatusId === ProgramRequestStatus.HarmonizationASOO
    && programRequestDetail.envelope?.programRequestTypeId === ProgramRequestType.ConsentForHarmonizationASOO;

  const { data: programs } = useGetProgramsByTypeAndDateQuery(
    { id: programRequest?.programTypeId!, expires: formatedReqestDate }
    , { skip: programRequest?.programTypeId! == null, });
  const { data: formsOfTeachingList } = useGetFormsOfTeachingQuery(formatedReqestDate);
  const { data: knowledgeChecksList } = useGetKnowledgeChecksQuery(formatedReqestDate);
  const { data: enrollmentConditionsList } = useGetEnrollmentConditionsQuery(formatedReqestDate);
  const { data: languageLevelsList } = useGetLanguageLevelsQuery(formatedReqestDate);

  const getProgramsSelectData = () => {
    if (programs == null) {
      return codeBookToSelectData(undefined);
    } else if (
      programRequest.programTypeId &&
      (
        programRequest.programTypeId === ProgramType.TrainingPrograms ||
        programRequest.programTypeId === ProgramType.CraftsAndArtsTrainingPrograms ||
        programRequest.programTypeId === ProgramType.CraftsSpecializationPrograms ||
        programRequest.programTypeId === ProgramType.RefinementPrograms        
      )
    ) {
      return codeBookToSelectData([
        { id: 0, name: t("common.nothingOffered") },
        ...programs,
      ]);
    } else {
      return codeBookToSelectData(programs);
    }
  };

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openUploadAdditionalDocumentationModal, setOpenUploadAdditionalDocumentationModal] = useState(false);

  const [sourceRequest, setSourceRequest] = useState<string>("");
  const isAsooCorrectionRole =
    userSession?.roleId === RoleEnum.ASOO ||
    userSession?.roleId === RoleEnum.ASOOAdmin;

  // ASOO additional documentation is editable only by the ASOO resolver during harmonization
  const canEditAsooAdditionalDocs = isAsooCorrectionRole && isHarmonizationAsooEdit;
  const asooAdditionalDocsCorrection =
    programRequestCorrection.additionalDocumentation?.filter(
      (doc) => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationASOO,
    ) ?? [];

  let locationOutsideHeadquartersIds: InstitutionLocation[] = [];

  const getSelectedNamesByIds = (
    items: Array<{ id: number; name: string }> | undefined,
    ids: number[] | undefined,
  ): string | undefined => {
    if (!items || !ids) {
      return undefined;
    }

    return ids
      .map((id) => items.find((item) => item.id === id)?.name)
      .filter((name): name is string => !!name)
      .join(", ");
  };

  // Two id selections are equal regardless of order — used so a reordered (but unchanged) list is not flagged as a diff
  const idSetsDiffer = (a?: number[], b?: number[]): boolean => {
    const setB = new Set(b ?? []);
    return (a?.length ?? 0) !== setB.size || !(a ?? []).every((id) => setB.has(id));
  };

  const { handleChange } = useInput();

  const [errors, doValidations] = useValidation({
    data: programRequestCorrection!,
    validationSchema: isAsooCorrectionRole
      ? ProgramRequestCorrectionValidationScheme
      : ResolverProgramRequestCorrectionValidationScheme,
  });

  useEffect(() => {
    const fetchProgramRequest = async () => {
      if (!userSession || (userSession.roleId !== RoleEnum.MZOM && userSession.roleId !== RoleEnum.MZOMAdmin && userSession.roleId !== RoleEnum.Helpdesk
        && userSession.roleId !== RoleEnum.ASOO && userSession.roleId !== RoleEnum.ASOOAdmin)) {
        return;
      }
      var results = await getProgramRequestDetail(parseInt(params.programRequestId));

      setProgramRequest(results);
      if (results && results.institutionId > 0) {
        var institutionResults = await getInstitutionRequest(results.institutionId);
        setInstitution(institutionResults);
      }

      if (results) {
        setProgramRequestDetail(results.programRequestDetail);
        let correctionData: ProgramRequestData;
        if (results.programRequestDetailCopy) {
          correctionData = mapProgramRequestData(results, results.programRequestDetailCopy);
        }
        else {
          correctionData = createProgramRequestCorrectionFromProgramRequest(results, results.programRequestDetail);
        }
        setProgramRequestCorrection(correctionData);
        setSourceRequest(JSON.stringify(correctionData));
      }
    }
    fetchProgramRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.programRequestId])

  if (programRequestDetail.outsideHeadquarter && institution.locations && programRequestDetail.outsideHeadquarterIds) {
    programRequestDetail.outsideHeadquarterIds.map(lid => {
      var outsideHeadquarterData = institution.locations.find((lc: { id: number; }) => lc.id === lid);
      if (outsideHeadquarterData)
        locationOutsideHeadquartersIds.push(outsideHeadquarterData);
      return lid;
    }
    );
  }

  const onBackToRequestList = () => {
    if (userSession) {
      if (userSession.roleId === RoleEnum.InstitutionAdmin || userSession.roleId === RoleEnum.InstitutionDirector) {
        history.push(dashboardRoute());
      }
      else {
        if (location.state && location.state.previousPage) {
          switch (location.state.previousPage) {
            case (PreviousPage.NISOO2ProgramRequestListPage): {
              history.push(newProgramRequestListRoute());
              break;
            }
            case (PreviousPage.ProgramRequestListPage):
            default: {
              history.push(programRequestListRoute());
              break;
            }
          }
        }
        else {
          history.push(programRequestListRoute());
        }
      }
    }
  };

  const onSubmitEditRequest = (event: React.MouseEvent<any, MouseEvent>) => {
    event.stopPropagation();
    const isValid = doValidations();
    if (!isValid) {
      return;
    }
    setOpenConfirmModal(true);
  };

  const onCancelEdit = (event: React.MouseEvent<any, MouseEvent>) => {
    event.stopPropagation();
    var currentRequest = (JSON.stringify(programRequestCorrection));

    if (currentRequest !== sourceRequest) {
      setOpenCancelModal(true);
    }
    else {
      exitPage();
    }
  };

  const handleCurriculumProgramAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {
    const newCurriculumProgram: DocumentData = {
      id: -1,
      fileName: file.name,
      file: file
    };
    setProgramRequestCorrection((prev) => ({
      ...prev,
      curriculumProgramDocument: newCurriculumProgram
    }));
  };

  const handleCurriculumProgramRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setProgramRequestCorrection((prev) => ({
      ...prev,
      curriculumProgramDocument: undefined,
    }));
  };

  const handleAdditionalDocumentationRemove = (index: number) => {
    if (index === -1) {
      return;
    }
    setProgramRequestCorrection((prev) => {
      const newAdditionalDocumentation = [...prev.additionalDocumentation];
      newAdditionalDocumentation.splice(index, 1);
      return { ...prev, additionalDocumentation: newAdditionalDocumentation };
    });
  };

  const handleAdditionalDocumentationDownload = (doc: DocumentData) => {
    if (doc.id === undefined) {
      return;
    }
    if (doc.id === -1) {
      saveAs(doc.file!, doc.fileName);
    } else {
      downloadDocument(DocumentType.AdditionalDocumentation, doc.id);
    }
  };

  const handleAdditionalDocumentationConfirm = (documents: DocumentData[]) => {
    if (documents && documents.length > 0) {
      const newAdditionalDocumentation: AdditionalDocumentation[] = documents.map((doc) => ({
        additionalDocumentationTypeId: AdditionalDocumentationType.AdditionalDocumentationASOO,
        additionalDocumentationDocument: doc,
      }));
      setProgramRequestCorrection((prev) => ({
        ...prev,
        additionalDocumentation: [...prev.additionalDocumentation, ...newAdditionalDocumentation],
      }));
    }
    setOpenUploadAdditionalDocumentationModal(false);
  };

  const uploadAdditionalDocumentationDocuments = async () => {
    const docs = programRequestCorrection.additionalDocumentation;
    if (!docs || docs.length === 0) {
      return;
    }
    const promises = docs.map(async (document, documentIndex) => {
      if (document.additionalDocumentationDocument.id !== -1) {
        return;
      }
      const uploadRequest: DocumentUploadRequest = {
        document: document.additionalDocumentationDocument.file!,
        documentTypeId: DocumentType.AdditionalDocumentation,
      };
      const idDoc = await uploadDocument(uploadRequest);
      setProgramRequestCorrection((prev) => {
        const newList = prev.additionalDocumentation.slice();
        newList[documentIndex].additionalDocumentationDocument.id = idDoc;
        return { ...prev, additionalDocumentation: newList };
      });
    });
    await Promise.all(promises);
  };

  const syncOtherOnIdsChange = (
    prevIds: number[],
    newIds: number[],
    prevOtherValues: string[] | undefined,
    otherId: number,
  ): string[] | undefined => {
    const hadOther = prevIds.includes(otherId);
    const hasOther = newIds.includes(otherId);
    if (!hadOther && hasOther) {
      return [""];
    }
    if (hadOther && !hasOther) {
      return [];
    }
    return prevOtherValues;
  };

  const handleFormsOfTeachingChange = (event: React.ChangeEvent<SelectEvent>) => {
    const newIds = event.target.value as number[];
    setProgramRequestCorrection((prev) => ({
      ...prev,
      formOfTeachingIds: newIds,
      otherFormsOfTeaching: syncOtherOnIdsChange(
        prev.formOfTeachingIds,
        newIds,
        prev.otherFormsOfTeaching,
        OtherFormOfTeachingId,
      ) ?? prev.otherFormsOfTeaching,
    }));
  };

  const handleKnowledgeChecksChange = (event: React.ChangeEvent<SelectEvent>) => {
    const newIds = event.target.value as number[];
    setProgramRequestCorrection((prev) => ({
      ...prev,
      knowledgeChecksIds: newIds,
      otherKnowledgeChecks: syncOtherOnIdsChange(
        prev.knowledgeChecksIds,
        newIds,
        prev.otherKnowledgeChecks,
        OtherKnowledgeCheckId,
      ) ?? prev.otherKnowledgeChecks,
    }));
  };

  const handleEnrollmentConditionsChange = (event: React.ChangeEvent<SelectEvent>) => {
    const newIds = event.target.value as number[];
    setProgramRequestCorrection((prev) => ({
      ...prev,
      enrollmentConditionsIds: newIds,
      otherEnrollmentConditions: syncOtherOnIdsChange(
        prev.enrollmentConditionsIds,
        newIds,
        prev.otherEnrollmentConditions,
        OtherEnrollmentConditionId,
      ) ?? prev.otherEnrollmentConditions,
    }));
  };

  const handleCancelClose = () => {
    setOpenCancelModal(false);
  }

  const handleConfirmedSuccessModal = () => {
    setOpenSuccessModal(false)
    exitPage();
  }

  const exitPage = () => {
    if (location.state && location.state.previousPage) {
      history.push(programRequestDetailsUrl(parseInt(params.programRequestId)), { previousPage: location.state.previousPage });
    }
    else {
      history.push(programRequestDetailsUrl(parseInt(params.programRequestId)));
    }
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    var programRequestId = parseInt(params.programRequestId);

    await uploadCurriculumProgramDocument();
    await uploadAdditionalDocumentationDocuments();

    const request = convertFromProgramRequest(programRequestCorrection);

    await createProgramRequestCorrection(programRequestId, request).then(() => {
      setOpenSuccessModal(true)
    });

    setOpenSuccessModal(true);
  }


  const uploadCurriculumProgramDocument = async () => {
    if (programRequestCorrection.curriculumProgramDocument && programRequestCorrection.curriculumProgramDocument.id === -1) {
      const curriculumDocumentRequest: DocumentUploadRequest = {
        document: programRequestCorrection.curriculumProgramDocument!.file!,
        documentTypeId: DocumentType.CurriculumProgram        
      };

      await uploadDocument(curriculumDocumentRequest).then((idDoc) => {
        var curriculumProgramDocument = programRequestCorrection.curriculumProgramDocument;
        curriculumProgramDocument!.id = idDoc;
        setProgramRequestCorrection((prev) => ({
          ...prev,
          curriculumProgramDocument: curriculumProgramDocument
        }));
      });
    }
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
    exitPage();
  }

  /*   const handleProgramChange = (event: any) => {
      const programId = event.target.value;
  
      var program = programs?.find(pr => pr.id === programId);
  
      var hkoLevel = program ? program.hkoLevelId : 0;
  
      setProgramRequestCorrection((prev) => ({
        ...prev,
        programId: programId,
        hkoLevelId: hkoLevel
      }));
    };
   */
  const getErrorForPath = (path: string): string | undefined => {
    path = path.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
    path = path.replace(/^\./, '');           // strip a leading dot
    var pathKeys = path.split('.');
    var result = errors as any;

    for (let i = 0; i < pathKeys.length; ++i) {
      var key = pathKeys[i];
      if (result && typeof (result) === 'object' && result.hasOwnProperty(key)) {
        result = result[key] as any;
      } else {
        return undefined;
      }
    }
    return result as string;
  }

  const programRequestCompetencesTableMetadata: TableMetadata<Competence> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "programRequestCompetence",
        header: t("request.competenceName"),
        accessor: (item: Competence) => {
          return (
            <div>
              <StaticField
                label={""}
                value={item.name}
                newValue={getCompetenceCorrection(item)}
                className={styles.changedCompetence}
              />
            </div>
          );
        }
      }
    ],
    rowToolTip: () => undefined
  }

  const getCompetenceCorrection = (item: Competence) => {
    if (programRequestDetail && programRequestDetail.competences && programRequestCorrection && programRequestCorrection.competences) {
      if (programRequestCorrection.competences.find((competence) => competence.name === item.name)) {
        return "";
      }

      var oldCompetence = programRequestDetail.competences.find((competence) => competence.id === item.id);
      var oldCompetenceIndex = programRequestDetail.competences.findIndex((competence) => competence.id === item.id);

      if (oldCompetence) {
        return programRequestCorrection.competences[oldCompetenceIndex]?.name;
      }
      return "";
    }
  }

  var [tableProps] = useTable<string>(
    {
      metadata: programRequestCompetencesTableMetadata,
      data: programRequestDetail.competences,
      pagination: false,
      totalItems: programRequestDetail.competences.length
    }
  );

  const convertStringToDate = (dateString: string) => {
    if (dateString) {
      var date = new Date(Date.parse(dateString));
      if (date.getFullYear() > 1900) {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      }
    }
    return undefined;
  };

  const getRoleName = (roleId: number) => {
    switch (roleId) {
      case RoleEnum.InstitutionAdmin:
        return t("common.institutionAdmin");
      case RoleEnum.MZOM:
        return t("common.MZOMAdmin");
      case RoleEnum.ASOO:
        return t("common.ASOO");
      case RoleEnum.Helpdesk:
        return t("common.Helpdesk");
      case RoleEnum.InstitutionDirector:
        return t("common.institutionDirector");
      case RoleEnum.ASOOAdmin:
        return t("common.ASOOManager");
      case RoleEnum.MZOMAdmin:
        return t("common.MZOManager");
      case RoleEnum.ASOOOffice:
        return t("common.ASOOOffice");
      case RoleEnum.MZOMOffice:
        return t("common.MZOOffice");
      default:
        return "";
    }
  }

  const getArrowButtonLabel = () => {
    if (location.state && location.state.previousPage) {
      switch (location.state.previousPage) {
        case PreviousPage.ProgramRequestListPage: {
          return t("common.returnToRequestList");
        }
        case PreviousPage.NISOO2ProgramRequestListPage: {
          return t("common.returnToNISOO2RequestList");
        }
        default: {
          return t("common.returnToRequestList");
        }
      }
    }
    return t("common.returnToRequestList");
  }

  let lastModifiedDate = hrDate(institution.lastModified);
  let lastModifiedHeader = t("institution.lastModifiedHeader") + ": " + lastModifiedDate;
  lastModifiedHeader += " (" + institution.user + ", " + getRoleName(institution.userRoleId) + ")";
  // lastModifiedHeader += " (" + institution.user + ", " + t("common.institutionAdministrator") + ")";

  return (
    <>
      <Row>
        <Col md={6}>
          <ArrowButton
            backwards
            ariaLabel=""
            onClick={onBackToRequestList}
          />
          <span className={styles.backButtonCaption}>
            {getArrowButtonLabel()}
          </span>

        </Col>
        {institution &&
          <Col md={6}>
            <div className={styles.institutionInfo}>
              <div className={styles.institutionInfoName}>{institution.name}</div>
              <div>{institution.oib}</div>
            </div>
          </Col>
        }
      </Row>

      {!userSession || (userSession.roleId !== RoleEnum.MZOM && userSession.roleId !== RoleEnum.MZOMAdmin && userSession.roleId !== RoleEnum.Helpdesk
        && userSession.roleId !== RoleEnum.ASOO && userSession.roleId !== RoleEnum.ASOOAdmin) &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("common.userNoPagePermisionMessage")}
              color={"danger"}
              fullWidth
            />
          </Col>
        </Row>
      }

      <ProgramRequestHeader
        programRequestId={programRequest.id}
        // programRequestDate={programRequest.requestDate}
        programRequestDate={programRequest.programDate}
        programRequestStatusString={programRequest.requestStatus}
        programRequestStatusId={programRequest.requestStatusId}
      />
      <Row>
        <Col md={12} className={styles.panelsCol}>
          <ExpandablePanel clickableHeader className={styles.panel}>
            <ExpandablePanelHeader className={styles.panelHeader}>
              {t("request.institutionDataPanelTitle")}
              <span className={styles.panelsSubHeader}>{lastModifiedHeader}</span>
            </ExpandablePanelHeader>
            <ExpandablePanelBody>
              <InstitutionPanel institution={institution} />
            </ExpandablePanelBody>
          </ExpandablePanel>
        </Col>
      </Row>

      <Row>
        <Col md={12} className={styles.panelsCol}>
          <ExpandablePanel expanded clickableHeader className={styles.panel}>
            <ExpandablePanelHeader className={styles.panelHeader}>
              {t("request.programDataPanelTitle")}
              {userSession && (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.Helpdesk
                || userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) &&
                <>
                  <Button onClick={onSubmitEditRequest} className={styles.editButton}>
                    {t("common.saveChanges")}
                  </Button>
                  <Button outline onClick={onCancelEdit} className={styles.editButton}>
                    {t("common.cancel")}
                  </Button>
                </>
              }
            </ExpandablePanelHeader>
            <ExpandablePanelBody className={styles.programPanelBody}>
              <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                  <Typography variant="smallHeading" component="h3">
                    {t("request.programBasicInfo")}
                  </Typography>
                </Col>
              </Row>
              <Row className={styles.dataRow}>
                <Col md={12}>
                  <StaticField
                    label={t("common.programType")}
                    value={programRequest.programTypeName}
                  />
                </Col>
              </Row>
              <Row className={styles.dataRow}>
                <Col md={4}>
                  {(programRequest.programTypeId === ProgramType.BasicArtsPrograms
                    || programRequest.programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears
                    || programRequest.programTypeId === ProgramType.CroatianLanguageForForeigners)
                    ? (
                      <StaticField
                        label={t("common.programDuration")}
                        value={programRequestDetail.guidedLearningDuration}
                        newValue={(userSession && userSession.roleId === RoleEnum.MZOM || userSession?.roleId === RoleEnum.MZOMAdmin || userSession?.roleId === RoleEnum.Helpdesk) ? programRequestCorrection?.guidedLearningDuration! : undefined}
                        className={styles.changedField}
                      />
                    ) : (
                      <StaticField
                        label={t("common.programDuration")}
                        value={
                          (Number(programRequestDetail.guidedLearningDuration) || 0) +
                          (Number(programRequestDetail.independentLearningDuration) || 0) +
                          (Number(programRequestDetail.workLearningDuration) || 0)
                        }
                      />
                    )
                  }
                </Col>
                {programRequestDetail.modules && programRequestDetail.modules.length > 0 &&
                  <Col md={4}>
                    <StaticField
                      label={t("request.CSVETTotal")}
                      value={programRequestDetail.modules.reduce((a, v) => a = a + v.learningSets.reduce((a, v) => a = a + (isNaN(v.csvet) ? 0 : v.csvet), 0), 0)}
                    />
                  </Col>
                }
              </Row>
              {(programRequest.programTypeId === ProgramType.BasicArtsPrograms
                || programRequest.programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears
                || programRequest.programTypeId === ProgramType.CroatianLanguageForForeigners)
                && userSession && (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) &&
                <Row className={styles.editRow}>
                  <Col md={4}>
                    <TextField
                      name={nameof<typeof programRequestCorrection>("guidedLearningDuration")}
                      type="number"
                      maxLength={5}
                      value={programRequestCorrection?.guidedLearningDuration!}
                      className={styles.durationInput}
                      errorText={errors?.guidedLearningDuration}
                      onChange={handleChange(setProgramRequestCorrection)}
                    />
                  </Col>
                </Row>
              }
              {programRequest.programTypeId !== ProgramType.BasicArtsPrograms
                && programRequest.programTypeId !== ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears
                && programRequest.programTypeId !== ProgramType.CroatianLanguageForForeigners &&
                <>
                  <Row className={styles.editRow}>
                    <Col md={4}>
                    </Col>
                    <Col md={4}>
                    </Col>
                  </Row>
                  <Row className={styles.dataRow}>
                    <Col md={4}>
                      <StaticField
                        label={t("request.guidedLearningDuration")}
                        value={programRequestDetail.guidedLearningDuration}
                        newValue={(userSession && userSession.roleId === RoleEnum.MZOM || userSession?.roleId === RoleEnum.MZOMAdmin || userSession?.roleId === RoleEnum.Helpdesk) ? programRequestCorrection?.guidedLearningDuration! : undefined}
                        className={styles.changedField}
                      />
                    </Col>
                    <Col md={4}>
                      <StaticField
                        label={t("request.workLearningDuration")}
                        value={programRequestDetail.workLearningDuration}
                        newValue={(userSession && userSession.roleId === RoleEnum.MZOM || userSession?.roleId === RoleEnum.MZOMAdmin || userSession?.roleId === RoleEnum.Helpdesk) ? programRequestCorrection?.workLearningDuration! : undefined}
                        className={styles.changedField}
                      />
                    </Col>
                    <Col md={4}>
                      <StaticField
                        label={t("request.independentLearningDuration")}
                        value={programRequestDetail.independentLearningDuration}
                        newValue={(userSession && userSession.roleId === RoleEnum.MZOM || userSession?.roleId === RoleEnum.MZOMAdmin || userSession?.roleId === RoleEnum.Helpdesk) ? programRequestCorrection?.independentLearningDuration! : undefined}
                        className={styles.changedField}
                      />
                    </Col>
                  </Row>
                  {userSession && (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) &&
                    <Row className={styles.editRow}>
                      <Col md={4}>
                        <TextField
                          name={nameof<typeof programRequestCorrection>("guidedLearningDuration")}
                          type="number"
                          maxLength={5}
                          value={programRequestCorrection?.guidedLearningDuration!}
                          className={styles.durationInput}
                          errorText={errors?.guidedLearningDuration}
                          onChange={handleChange(setProgramRequestCorrection)}
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          name={nameof<typeof programRequestCorrection>("workLearningDuration")}
                          type="number"
                          maxLength={5}
                          value={programRequestCorrection?.workLearningDuration!}
                          className={styles.durationInput}
                          errorText={errors?.workLearningDuration}
                          onChange={handleChange(setProgramRequestCorrection)}
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          name={nameof<typeof programRequestCorrection>("independentLearningDuration")}
                          type="number"
                          maxLength={5}
                          value={programRequestCorrection?.independentLearningDuration!}
                          className={styles.durationInput}
                          errorText={errors?.independentLearningDuration}
                          onChange={handleChange(setProgramRequestCorrection)}
                        />
                      </Col>
                    </Row>
                  }
                </>
              }
              <Row className={styles.dataRow}>
                <Col md={8}>
                  <StaticField
                    label={t("common.programName")}
                    value={programRequestDetail.programName}
                  />
                </Col>
                <Col md={4}>
                  <StaticField
                    label={t("codeBook.programCode")}
                    value={programRequestDetail.programCode}
                  />
                </Col>
              </Row>
              {/*               <Row className={styles.editRow}>
                <Col md={8}>
                  <Autocomplete
                    label={t("common.programName")}
                    name={nameof<typeof programRequestCorrection>("programId")}
                    value={programRequestCorrection.programId}
                    onChange={handleProgramChange}
                    selectData={getProgramsSelectData()}
                  />
                </Col>
              </Row>

              
 */}



              {(programRequestCorrection.programId === null || programRequestCorrection.programId === 0) &&
                <ExpertiseEditProgramInfo
                  programTypeId={programRequest.programTypeId}
                  programRequest={programRequestDetail}
                  programRequestCorrection={programRequestCorrection}
                  onChange={handleChange(setProgramRequestCorrection)}
                  saveProgramRequest={setProgramRequestCorrection}
                  errors={errors}
                />
              }
              {!(programRequestCorrection.programId === null || programRequestCorrection.programId === 0) && (
                <ExistingProgramInfo
                  programTypeId={programRequest.programTypeId}
                  programId={programRequestCorrection?.programId!}
                  programData={programs?.find(pr => pr.id === programRequestCorrection.programId)}
                />
              )}

              {(programRequest.programTypeId === ProgramType.ForeignLanguageLearningPrograms
                || programRequest.programTypeId === ProgramType.CroatianLanguageForForeigners) &&
                <Row className={styles.dataRow}>
                  <Col md={4}>
                    <StaticField
                      label={t("common.language")}
                      value={programs?.find(pr => pr.id === programRequestDetail.programId)?.languageName}
                    />
                  </Col>
                  <Col md={4}>
                    <StaticField
                      label={t("request.languageLevelGeneral")}
                      value={programRequestDetail.languageLevelGeneral?.map(ll => ll).join(", ") ?? ""}
                    />
                  </Col>
                  <Col md={4}>
                    <StaticField
                      label={t("request.languageLevelBusiness")}
                      value={programRequestDetail.languageLevelBusiness?.map(ll => ll).join(", ") ?? ""}
                    />
                  </Col>
                </Row>
              }
              {(programRequest.programTypeId === ProgramType.ForeignLanguageLearningPrograms
                || programRequest.programTypeId === ProgramType.CroatianLanguageForForeigners) && userSession 
                && (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) &&
                <Row className={styles.editRow}>
                  <Col md={4}>
                  </Col>
                  <Col md={4}>
                    <Select
                      multiple
                      name={nameof<typeof programRequestCorrection>("languageLevelGeneralIds")}
                      value={programRequestCorrection.languageLevelGeneralIds}
                      onChange={handleChange(setProgramRequestCorrection)}
                      selectData={codeBookToSelectData(languageLevelsList)}
                    />
                  </Col>
                  <Col md={4}>
                    <Select
                      multiple
                      name={nameof<typeof programRequestCorrection>("languageLevelBusinessIds")}
                      value={programRequestCorrection.languageLevelBusinessIds}
                      onChange={handleChange(setProgramRequestCorrection)}
                      selectData={codeBookToSelectData(languageLevelsList)}
                    />
                  </Col>
                </Row>
              }
              <Row className={styles.dataRow}>
                <Col md={4}>
                  <StaticField
                    label={t("request.formOfTeaching")}
                    value={programRequestDetail.formOfTeaching?.map(kc => kc).join(", ") ?? ""}
                    newValue={(userSession && (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.Helpdesk)
                      && idSetsDiffer(programRequestDetail.formOfTeachingIds, programRequestCorrection?.formOfTeachingIds))
                      ? getSelectedNamesByIds(formsOfTeachingList, programRequestCorrection?.formOfTeachingIds)
                      : undefined}
                    className={styles.changedField}
                  />
                </Col>
                <Col md={4}>
                  <StaticField
                    label={t("request.knowledgeChecks")}
                    value={programRequestDetail.knowledgeChecks?.map(kc => kc).join(", ") ?? ""}
                    newValue={(userSession && (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.Helpdesk)
                      && idSetsDiffer(programRequestDetail.knowledgeChecksIds, programRequestCorrection?.knowledgeChecksIds))
                      ? getSelectedNamesByIds(knowledgeChecksList, programRequestCorrection?.knowledgeChecksIds)
                      : undefined}
                    className={styles.changedField}
                  />
                </Col>
                <Col md={4}>
                  <StaticField
                    label={t("request.enrollmentConditions")}
                    value={programRequestDetail.enrollmentConditions?.map(ec => ec).join(", ") ?? ""}
                    newValue={(userSession && (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.Helpdesk)
                      && idSetsDiffer(programRequestDetail.enrollmentConditionsIds, programRequestCorrection?.enrollmentConditionsIds))
                      ? getSelectedNamesByIds(enrollmentConditionsList, programRequestCorrection?.enrollmentConditionsIds)
                      : undefined}
                    className={styles.changedField}
                  />
                </Col>
              </Row>
              {userSession && (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) &&
                <Row className={styles.wideEditRow}>
                  <Col md={4}>
                    <Select
                      multiple
                      name={nameof<typeof programRequestCorrection>("formOfTeachingIds")}
                      value={programRequestCorrection.formOfTeachingIds}
                      onChange={handleFormsOfTeachingChange}
                      selectData={codeBookToSelectData(formsOfTeachingList)}
                      errorText={errors?.formOfTeachingIds?.toString()}
                    />
                  </Col>
                  <Col md={4}>
                    <Select
                      multiple
                      name={nameof<typeof programRequestCorrection>("knowledgeChecksIds")}
                      value={programRequestCorrection.knowledgeChecksIds}
                      onChange={handleKnowledgeChecksChange}
                      selectData={codeBookToSelectData(knowledgeChecksList)}
                      errorText={errors?.knowledgeChecksIds?.toString()}
                    />
                  </Col>
                  <Col md={4}>
                    <Select
                      multiple
                      name={nameof<typeof programRequestCorrection>("enrollmentConditionsIds")}
                      value={programRequestCorrection.enrollmentConditionsIds}
                      onChange={handleEnrollmentConditionsChange}
                      selectData={codeBookToSelectData(enrollmentConditionsList)}
                      errorText={errors?.enrollmentConditionsIds?.toString()}
                    />
                  </Col>
                </Row>
              }
              {(programRequestCorrection.formOfTeachingIds.includes(OtherFormOfTeachingId) || programRequestDetail.formOfTeachingIds.includes(OtherFormOfTeachingId)) &&
                <Row className={styles.dataRow}>
                  <Col md={12}>
                    <StaticField
                      label={t("request.otherFormsOfTeaching")}
                      value={programRequestDetail.otherFormsOfTeaching.toString().replace(/,/g, ", ")}
                    />
                  </Col>
                </Row>
              }
              {userSession && (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) && programRequestCorrection.formOfTeachingIds.includes(OtherFormOfTeachingId) &&
                <Row className={styles.editRow}>
                  <Col md={12}>
                    <OtherData
                      name={nameof<typeof programRequestCorrection>("otherFormsOfTeaching")}
                      header={t("request.formOfTeaching")}
                      label={t("request.otherFormsOfTeaching")}
                      addButtonLabel={t("request.addNewFormOfTeaching")}
                      values={programRequestCorrection.otherFormsOfTeaching}
                      isCorrection={true}
                      onChange={handleChange(setProgramRequestCorrection)}
                      getErrorForPath={getErrorForPath}
                    />
                  </Col>
                </Row>
              }
              {
                (programRequestCorrection.knowledgeChecksIds.includes(OtherKnowledgeCheckId) || programRequestDetail.knowledgeChecksIds.includes(OtherKnowledgeCheckId)) &&
                <Row className={styles.dataRow}>
                  <Col md={12}>
                    <StaticField
                      label={t("request.otherKnowledgeChecks")}
                      value={programRequestDetail.otherKnowledgeChecks.toString().replace(/,/g, ", ")}
                    />
                  </Col>
                </Row>
              }
              {userSession && (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) && programRequestCorrection.knowledgeChecksIds.includes(OtherKnowledgeCheckId) &&
                <Row className={styles.editRow}>
                  <Col md={12}>
                    <OtherData
                      name={nameof<typeof programRequestCorrection>("otherKnowledgeChecks")}
                      header={t("request.knowledgeChecks")}
                      label={t("request.otherKnowledgeChecks")}
                      addButtonLabel={t("request.addNewKnowledgeCheck")}
                      values={programRequestCorrection.otherKnowledgeChecks}
                      isCorrection={true}
                      onChange={handleChange(setProgramRequestCorrection)}
                      getErrorForPath={getErrorForPath}
                    />
                  </Col>
                </Row>

              }
              {
                (programRequestCorrection.enrollmentConditionsIds.includes(OtherEnrollmentConditionId) || programRequestDetail.enrollmentConditionsIds.includes(OtherEnrollmentConditionId)) &&
                <Row className={styles.dataRow}>
                  <Col md={12}>
                    <StaticField
                      label={t("request.otherEnrollmentConditions")}
                      value={programRequestDetail.otherEnrollmentConditions?.toString().replace(/,/g, ", ") ?? ""}
                    />
                  </Col>
                </Row>
              }
              {userSession && (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) && programRequestCorrection.enrollmentConditionsIds.includes(OtherEnrollmentConditionId) &&
                <Row className={styles.editRow}>
                  <Col md={12}>
                    <OtherData
                        name={nameof<typeof programRequestCorrection>("otherEnrollmentConditions")}
                        header={t("request.enrollmentConditions")}
                        label={t("request.otherEnrollmentConditions")}
                        addButtonLabel={t("request.addNewEnrollmentCondition")}
                        values={programRequestCorrection.otherEnrollmentConditions}
                        isCorrection={true}
                        onChange={handleChange(setProgramRequestCorrection)}
                        getErrorForPath={getErrorForPath}
                    />
                  </Col>
                </Row>
              }

              {userSession && (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.Helpdesk) && programRequestDetail.competences.length > 0 &&
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.competences")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Table {...tableProps} />
                    </Col>
                  </Row>
                </>
              }
              {userSession && (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) &&
                programRequestCorrection.competences.length > 0 &&
                <CompetenceInfoMzo
                  name={`${nameof<typeof programRequestCorrection>("competences")}`}
                  getErrorForPath={getErrorForPath}
                  competences={programRequestDetail.competences}
                  competencesCorrections={programRequestCorrection.competences}
                  onChange={handleChange(setProgramRequestCorrection)} />
              }

              {programRequestCorrection.teachers.length > 0 && (isHarmonizationAsooEdit
                ? <ProgramRequestTeacherPanel
                  requestTeachers={programRequestDetail.programRequestTeachers}
                />
                : <ProgramRequestTeacherMzo
                  name={`${nameof<typeof programRequestCorrection>("teachers")}`}
                  programRequestTeachers={programRequestDetail.programRequestTeachers}
                  programRequestTeacherCorrection={programRequestCorrection.teachers}
                  onChange={handleChange(setProgramRequestCorrection)}
                  getErrorForPath={getErrorForPath}
                />)
              }

              {canEditAsooAdditionalDocs ?
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.additionalDocumentationASOO")}
                      </Typography>
                    </Col>
                  </Row>
                  {asooAdditionalDocsCorrection.length > 0 &&
                    <Row className={styles.dataRow}>
                      <Col md={12}>
                        <DocumentList
                          documents={asooAdditionalDocsCorrection.map((doc) => doc.additionalDocumentationDocument)}
                          removable
                          onRemoveClick={(doc) => handleAdditionalDocumentationRemove(
                            programRequestCorrection.additionalDocumentation.findIndex((d) => d.additionalDocumentationDocument === doc),
                          )}
                          onDocumentClick={(doc) => handleAdditionalDocumentationDownload(doc)}
                        />
                      </Col>
                    </Row>
                  }
                  <Row className={styles.dataRow}>
                    <Col md={4}>
                      <Button
                        outline
                        color={"primary"}
                        icon={<UploadIcon />}
                        onClick={() => setOpenUploadAdditionalDocumentationModal(true)}
                      >
                        {t("common.addDocument")}
                      </Button>
                    </Col>
                  </Row>
                </>
                : programRequestDetail.additionalDocumentation
                && programRequestDetail.additionalDocumentation.filter(doc => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationASOO).length > 0 &&
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.additionalDocumentationASOO")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className={styles.dataRow}>
                    <Col md={12}>
                      <DocumentList
                        documents={programRequestDetail.additionalDocumentation.filter(doc => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationASOO).map(dc => dc.additionalDocumentationDocument)}
                        removable={false}
                        onDocumentClick={(document) => downloadDocument(DocumentType.AdditionalDocumentation, document.id!)}
                      />
                    </Col>
                  </Row>
                </>
              }

              {programRequestDetail.additionalDocumentation 
                && programRequestDetail.additionalDocumentation.filter(doc => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationMZOM).length > 0 &&
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.additionalDocumentationMZOM")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className={styles.dataRow}>
                    <Col md={12}>
                      <DocumentList                        
                        documents={programRequestDetail.additionalDocumentation.filter(doc => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationMZOM).map(dc => dc.additionalDocumentationDocument)}
                        removable={false}
                        onDocumentClick={(document) => downloadDocument(DocumentType.AdditionalDocumentation, document.id!)}
                      />
                    </Col>
                  </Row>
                </>
              }


              {programRequestCorrection && programRequestCorrection.modules.length > 0 &&
                <ProgramRequestModuleEditor
                  programRequestId={programRequest.id}
                  requestModules={programRequestDetail.modules}
                  requestCorrectionModules={programRequestCorrection.modules}
                  readonly={isSubmittedMzoStatus}
                  getErrorForPath={getErrorForPath}
                  onChange={handleChange(setProgramRequestCorrection)}
                />
              }
              <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                  <Typography variant="smallHeading" component="h3">
                    {t("request.curriculum")}
                  </Typography>
                </Col>
              </Row>
              <Row className={styles.dataRow}>
                {(userSession?.roleId !== RoleEnum.ASOO && userSession?.roleId !== RoleEnum.ASOOAdmin) &&
                  <Col md={3} className={styles.hideUploadButton}>
                    <UploadButton
                      name={nameof<typeof programRequestCorrection>("curriculumProgramDocument")}
                      value={programRequestCorrection.curriculumProgramDocument}
                      disabled={true}
                      documentType={DocumentType.CurriculumProgram}
                    />
                  </Col>
                }
                {(userSession?.roleId === RoleEnum.ASOO || userSession?.roleId === RoleEnum.ASOOAdmin) &&
                  <Col md={3}>
                    <UploadButton
                      name={nameof<typeof programRequestCorrection>("curriculumProgramDocument")}
                      value={programRequestCorrection.curriculumProgramDocument}
                      //disabled={programRequestDetail.curriculumProgramDocument !== undefined}
                      onFileAdded={handleCurriculumProgramAdded}
                      onFileRemoved={handleCurriculumProgramRemoved}
                      documentType={DocumentType.CurriculumProgram}
                      toolTip={t("document.wordAndPdfTooltip")}
                      fileFilter={t("document.wordAndPdfFilter")}
                      errorText={errors?.curriculumProgramDocument}
                    />
                  </Col>
                }
              </Row>

              {(programRequestDetail.locationHeadquarter) && (
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.headquarterExecutionPlaceTitle")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className={styles.dataRow}>
                    <Col md={3}>
                      <StaticField
                        label={t("common.address")}
                        value={institution.address}
                      />
                    </Col>
                    <Col md={3}>
                      <StaticField
                        label={t("common.town")}
                        value={institution.town.name}
                      />
                    </Col>
                    <Col md={3}>
                      <StaticField
                        label={t("common.municipality")}
                        value={institution.town.municipalityName}
                      />
                    </Col>
                    <Col md={3}>
                      <StaticField
                        label={t("common.county")}
                        value={institution.town.countyName}
                      />
                    </Col>
                  </Row>
                </>
              )}
              {(programRequestDetail.outsideHeadquarter && locationOutsideHeadquartersIds.length > 0) && (
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.outsideHeadquarterExecutionPlaceTitle")}
                      </Typography>
                    </Col>
                  </Row>

                  {locationOutsideHeadquartersIds.map(lc => (
                    <Row className={styles.dataRow}>
                      <Col md={3}>
                        <StaticField
                          label={t("common.address")}
                          value={lc.address}
                        />
                      </Col>
                      <Col md={3}>
                        <StaticField
                          label={t("common.town")}
                          value={lc.town.name}
                        />
                      </Col>
                      <Col md={3}>
                        <StaticField
                          label={t("common.municipality")}
                          value={lc.town.municipalityName}
                        />
                      </Col>
                      <Col md={3}>
                        <StaticField
                          label={t("common.county")}
                          value={lc.town.countyName}
                        />
                      </Col>
                    </Row>

                  ))}

                </>
              )}
              {(programRequestDetail.locationOtherLegalEntity) && (
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t(
                          "request.anotherLegalEntityLocationExecutionPlaceTitle"
                        )}
                      </Typography>
                    </Col>
                  </Row>

                  {programRequestDetail.otherLegalEntityLocations &&
                    programRequestDetail.otherLegalEntityLocations.length > 0 &&
                    programRequestDetail.otherLegalEntityLocations.map(
                      (otherLegalEntityLocation, otherLocationIndex) => {
                        return (
                          <OtherLegalEntityPanel otherLegalEntityLocation={otherLegalEntityLocation} />
                        );
                      }
                    )}
                </>
              )}

              <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                  <Typography variant="smallHeading" component="h3">
                    {t("common.note")}
                  </Typography>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <TextArea
                    label=""
                    name={nameof<typeof programRequestCorrection>("remark")}
                    value={programRequestCorrection.remark}
                    onChange={handleChange(setProgramRequestCorrection)}
                    ariaLabel={t("common.note")}
                    maxLength={700}
                    rows={4}
                  />
                </Col>
              </Row>
            </ExpandablePanelBody>
          </ExpandablePanel>
        </Col>
      </Row>

      <Row>
        <Col md={12} className={styles.panelsCol}>
          <ExpandablePanel clickableHeader className={styles.panel}>
            <ExpandablePanelHeader className={styles.panelHeader}>
              {t("request.historyofChanges")}
            </ExpandablePanelHeader>
            <ExpandablePanelBody>
              <ProgramRequestHistoryPanel programRequestId={programRequest.id} requestHistory={programRequest.programRequestStatusHistorys} />
            </ExpandablePanelBody>
          </ExpandablePanel>
        </Col>
      </Row>

      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.yesSaveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureSaveChanges")}
          </Typography>
        </div>
      </Modal>
      <Modal
        type="confirm"
        open={openCancelModal}
        cancelText={t("common.noProperCase")}
        confirmText={t("common.yesCancel")}
        onClose={handleCancelClose}
        onConfirm={exitPage}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureCancelChanges")}
          </Typography>
        </div>
        <div>
          <Typography component="h4" variant="medium">
            {t("common.areYouSureCancelChangesNote")}
          </Typography>
        </div>
      </Modal>
      <Modal
        type="message"
        open={openSuccessModal}
        confirmText={t("common.close")}
        onClose={handleConfirmedSuccessModal}
        onConfirm={handleConfirmedSuccessModal}
      >
        <div className={styles.modalMessageContentAlignCenter}>

          <Typography component="h1" variant="largeBold">
            {t("common.saveSucceed")}
          </Typography>
        </div>
      </Modal>
      <DocumentsUploadModal
        title={t("request.additionalDocumentationASOO")}
        infoPanelText={t("request.additionalDocumentationPanelASOO")}
        types={t("document.imagesAndPdfAndWordFilter")}
        allDocuments={programRequestCorrection.additionalDocumentation.filter((doc) => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationASOO).map((doc) => doc.additionalDocumentationDocument)}
        onRemoveUploadedDocument={(filteredIndex: number) => {
          const docsOfType = programRequestCorrection.additionalDocumentation.filter((doc) => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationASOO);
          const target = docsOfType[filteredIndex];
          handleAdditionalDocumentationRemove(target ? programRequestCorrection.additionalDocumentation.indexOf(target) : -1);
        }}
        maxSize={10}
        maxFiles={programRequestCorrection.additionalDocumentation ? 10 - programRequestCorrection.additionalDocumentation.filter((doc) => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationASOO).length : 10}
        filesAction={undefined}
        open={openUploadAdditionalDocumentationModal}
        onConfirm={handleAdditionalDocumentationConfirm}
        onClose={() => setOpenUploadAdditionalDocumentationModal(false)}
        hideOldDocuments={false}
        btnCancelText={t("common.cancel")}
        btnOkText={t("common.addNew")}
      />
    </>
  );
}

